/**
 * Validated environment variables.
 *
 * Imported first in main.tsx so a missing or malformed config fails fast
 * at startup with a readable error rather than a blank screen at runtime.
 */

function requireEnv(key: string): string {
  const value = import.meta.env[key] as string | undefined
  if (!value) {
    throw new Error(
      `[env] Missing required environment variable: ${key}\n` +
        `  Set it in your .env file or deployment environment and rebuild.`,
    )
  }
  return value
}

function requireUrl(key: string): string {
  const value = requireEnv(key)
  try {
    new URL(value)
  } catch {
    throw new Error(
      `[env] Environment variable ${key} must be a valid URL, got: ${JSON.stringify(value)}`,
    )
  }
  return value
}

// Empty string means "serve assets from the same origin as the app" — valid in
// CloudFront-hosted builds where the app and assets share a distribution, and
// in `npm run dev` where vite.config.ts proxies /previews to staging CloudFront.
function optionalUrl(key: string): string {
  const value = (import.meta.env[key] as string | undefined) ?? ''
  if (value === '') return value
  try {
    new URL(value)
  } catch {
    throw new Error(
      `[env] Environment variable ${key} must be a valid URL or empty, got: ${JSON.stringify(value)}`,
    )
  }
  return value
}

function optionalEnv(key: string): string {
  return (import.meta.env[key] as string | undefined) ?? ''
}

export const env = {
  VITE_API_BASE_URL: requireUrl('VITE_API_BASE_URL'),
  VITE_ASSETS_BASE_URL: optionalUrl('VITE_ASSETS_BASE_URL'),
  VITE_COGNITO_USER_POOL_ID: requireEnv('VITE_COGNITO_USER_POOL_ID'),
  VITE_COGNITO_APP_CLIENT_ID: requireEnv('VITE_COGNITO_APP_CLIENT_ID'),
  VITE_MAPLIBRE_STYLE_URL: requireUrl('VITE_MAPLIBRE_STYLE_URL'),
  /** Keyless basemap served to unpaid users so Explore costs no MapTiler tiles.
   * Optional: defaults to OpenFreeMap Liberty in `mapStyles.ts`. Override only to
   * point at a different free/self-hosted style — it must NOT be a MapTiler URL
   * (it deliberately bypasses the key-carrying `mapStyleUrl()` rewrite). */
  VITE_FREE_BASEMAP_STYLE_URL: optionalUrl('VITE_FREE_BASEMAP_STYLE_URL'),
  /** MapTiler API key for client-side geocoding. Optional: if empty, the key is
   * extracted from VITE_MAPLIBRE_STYLE_URL's `key` query param at runtime. */
  VITE_MAPTILER_KEY: optionalEnv('VITE_MAPTILER_KEY'),
  /** F07 feature flag for the live terrain preview. `'true'` enables it;
   * anything else (incl. unset) leaves the preview UI dark. */
  VITE_TERRAIN_PREVIEW: optionalEnv('VITE_TERRAIN_PREVIEW'),
  /** Public maker directory. Only literal 'true' enables; ships dark. */
  VITE_MAKER_DIRECTORY: optionalEnv('VITE_MAKER_DIRECTORY'),
  /** Video creator rewards (annual-and-puzzle-unlock). Only literal 'true'
   * enables; ships dark. */
  VITE_CREATOR_REWARDS: optionalEnv('VITE_CREATOR_REWARDS'),
  /** Share cards (docs/tickets/share-cards). Only literal 'true' enables;
   * ships dark. */
  VITE_SHARE_CARDS: optionalEnv('VITE_SHARE_CARDS'),
  /** My filaments (docs/tickets/owned-filaments). Only literal 'true' enables;
   * ships dark. */
  VITE_OWNED_FILAMENTS: optionalEnv('VITE_OWNED_FILAMENTS'),
  /** Cost kill-switch: force EVERY tier (paid included) to the free basemap on
   * Explore, cutting MapTiler tile spend on the unbounded surface to zero. The
   * builder is unaffected. `'true'` enables it; anything else (incl. unset)
   * leaves today's per-tier policy in place. See `lib/mapAccess.ts`. */
  VITE_EXPLORE_MAP_ONLY: optionalEnv('VITE_EXPLORE_MAP_ONLY'),
  /** Upgrade destination for tier-locked CTAs (C2). Points at the MakerWorld
   * crowdfunding campaign (no self-serve billing). Override with VITE_UPGRADE_URL
   * if the campaign URL changes; defaulting happens in `lib/flags.ts`. */
  VITE_UPGRADE_URL: optionalEnv('VITE_UPGRADE_URL'),
  /** Thin-kerf assembled-layout lock (Bambu export modal). Defaults ON; only
   * the literal `'false'` lifts it so any kerf can be printed assembled.
   * Frontend-only — the backend does not re-enforce it. */
  VITE_FORCE_SEPARATE_THIN_KERF: optionalEnv('VITE_FORCE_SEPARATE_THIN_KERF'),
  /** Code-optional signup (stripe-subscriptions C5 — UI side of B7's backend
   * ALLOW_OPEN_SIGNUP). `'true'` makes the register access-code field optional;
   * anything else (incl. unset) keeps the code required. Ships dark; Phase 5 (D)
   * flips it alongside the backend flag. */
  VITE_OPEN_SIGNUP: optionalEnv('VITE_OPEN_SIGNUP'),
  /** PostHog project key (O3). Optional: when empty, analytics is a no-op so
   * local dev + CI never phone home. `track()` still logs to the console and
   * dispatches the `t6:analytics` DOM event. */
  VITE_POSTHOG_KEY: optionalEnv('VITE_POSTHOG_KEY'),
  /** PostHog ingestion host. Defaults to EU cloud in code (`eu.i.posthog.com`)
   * when unset but the key is set (GDPR residency posture). */
  VITE_POSTHOG_HOST: optionalEnv('VITE_POSTHOG_HOST'),
  /** Sentry DSN (O3). Optional: empty = error reporting is a no-op (console only). */
  VITE_SENTRY_DSN: optionalEnv('VITE_SENTRY_DSN'),
  /** Cloudflare Turnstile PUBLIC site key — the bot gate on the open (codeless)
   * signup form. Safe in the bundle by design (only the secret key, held in
   * Secrets Manager and read by the Pre-Signup trigger, verifies the token).
   * Optional: empty renders no widget, so signup carries no token — which the
   * backend rejects unless its Turnstile secret is likewise unconfigured. */
  VITE_TURNSTILE_SITE_KEY: optionalEnv('VITE_TURNSTILE_SITE_KEY'),
} as const
